// Thumbnail-driven product gallery: thumbnails scroll the snap track to their slide on click
// (custom --show-slide command) and on mouse hover, and the visible slide marks its thumbnail as current.
class SProductThumbnails extends HTMLElement {
  #ac;
  #observer;
  #hoverTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    // Command events fire on the track (the commandfor target) and don't bubble, so handle clicks directly.
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      event.preventDefault();
      clearTimeout(this.#hoverTimer);
      this.#show(thumb.dataset.thumb);
    }, { signal });

    // Hover preview for mouse users; a short delay avoids flicker while the cursor passes over the rail.
    this.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const thumb = event.target.closest('[data-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = setTimeout(() => this.#show(thumb.dataset.thumb), 80);
    }, { signal });

    this.addEventListener('pointerout', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (event.target.closest('[data-thumb]')) clearTimeout(this.#hoverTimer);
    }, { signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#setCurrent(entry.target.dataset.slide);
      }
    }, { root: this.track, threshold: 0.6 });

    this.querySelectorAll('[data-slide]').forEach((slide) => this.#observer.observe(slide));

    // Variant media: open on the selected variant's photo, and follow colour/option changes.
    let variantSlides = {};
    try {
      variantSlides = JSON.parse(this.dataset.variantSlides || '{}');
    } catch {
      variantSlides = {};
    }

    const initial = this.dataset.initialSlide;
    if (initial && initial !== '1') {
      const slide = this.querySelector(`[data-slide="${initial}"]`);
      if (slide) {
        requestAnimationFrame(() => {
          this.track.scrollLeft = slide.offsetLeft;
          this.#setCurrent(initial);
        });
      }
    }

    document.addEventListener('change', (event) => {
      const input = event.target;
      const picker = input.closest?.('s-variant-picker');
      if (!picker || picker.dataset.productId !== this.dataset.productId) return;
      const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      const position = variantSlides[variantId];
      if (position) this.#show(String(position));
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#hoverTimer);
  }

  #show(index) {
    const slide = this.querySelector(`[data-slide="${index}"]`);
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  }

  #setCurrent(index) {
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      if (thumb.dataset.thumb === String(index)) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-thumbnails')) {
  customElements.define('s-product-thumbnails', SProductThumbnails);
}
